import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import {
  SummaryBoxColorScheme,
  SummaryBoxColorSchemeClasses,
  SummaryBoxType,
  SummaryBoxTypeClasses,
} from 'teacher-ui-client/components/giraffe/summary-box/themes'
import { LinkTo } from '@ember/routing'
import type { SafeString } from '@ember/template'

export interface Stat {
  value: string | SafeString
  label: string
}

interface Args {
  colorScheme?: SummaryBoxColorScheme
  grade?: string
  link?: {
    route: string
    text: string
  }
  stats?: Stat[]
  title?: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
  Blocks: {
    default: []
  }
}

/**
 * Summary box chart. Takes a list of stat objects and display them.
 */
export class SummaryBox extends Component<Signature> {
  get stats() {
    return this.args.stats ?? []
  }

  get statsType() {
    switch (this.stats.length) {
      case 1:
        return SummaryBoxType.Single
      case 2:
        return SummaryBoxType.Double
      case 3:
        return SummaryBoxType.Triple
      default:
        return SummaryBoxType.Many
    }
  }

  get statsView() {
    return this.stats.map((statItem, index) => {
      return {
        ...statItem,
        id: index + 1,
        showHorizontalDivider:
          (this.isDouble && index === 1) || (this.isTriple && index === 2) || (this.isMany && index !== 0),
        showVerticalDivider: this.isTriple && index === 0,
      }
    })
  }

  get isDouble() {
    return this.statsType === SummaryBoxType.Double
  }

  get isTriple() {
    return this.statsType === SummaryBoxType.Triple
  }

  get isMany() {
    return this.statsType === SummaryBoxType.Many
  }

  get statTypeClasses() {
    const statKey = this.statsType
    return SummaryBoxTypeClasses[statKey]
  }

  get colorSchemeClasses() {
    const colorScheme = this.args.colorScheme ?? SummaryBoxColorScheme.Default
    return SummaryBoxColorSchemeClasses[colorScheme]
  }
  <template>
    <div data-test-summary-box class="flex flex-col overflow-hidden rounded-l-md print:rounded-none" ...attributes>
      <div
        data-test-summary-box-header
        class="px-4 py-3
          {{this.colorSchemeClasses.header}}
          print:border-dusty-black-200 print:bg-dusty-black-50 print:m-px print:w-[calc(100%-2px)] print:overflow-hidden print:border-b"
      >
        {{#if @title}}
          <h5 data-test-summary-box-title class="my-1 text-white print:text-black">
            {{@title}}
          </h5>
          {{#if @grade}}
            <p class="text-xl font-bold text-white print:text-black">
              {{@grade}}
            </p>
          {{/if}}
        {{else}}
          <h5 data-test-summary-box-title class="my-1 text-white print:text-black">
            {{@grade}}
          </h5>
        {{/if}}
      </div>
      {{#if (has-block)}}
        <div class="flex grow flex-col px-4 py-3 print:bg-transparent {{this.colorSchemeClasses.stats}}">
          {{yield}}
        </div>
      {{else}}
        <div
          class="{{this.statTypeClasses.statWrapper}} px-4 py-3 print:bg-transparent {{this.colorSchemeClasses.stats}}"
        >
          {{#each this.statsView as |stat|}}
            {{#if stat.showHorizontalDivider}}
              <hr class="m-0 w-full {{this.colorSchemeClasses.dividerBorder}} print:border-dusty-black-200" />
            {{/if}}

            <div data-test-summary-box-stat="{{stat.id}}" class="{{this.statTypeClasses.statBox}}">
              <p data-test-summary-box-stat-value class="{{this.statTypeClasses.statTitle}} m-0 font-extralight">
                {{stat.value}}
              </p>
              <p data-test-summary-box-stat-label class="text-base font-medium print:font-bold!">
                {{stat.label}}
              </p>
              {{#if stat.showVerticalDivider}}
                <div
                  class="absolute top-4 right-0 bottom-4 mx-3 w-px
                    {{this.colorSchemeClasses.dividerBg}}
                    print:bg-dusty-black-200"
                ></div>
              {{/if}}
            </div>
          {{/each}}

          {{#if @link}}
            <LinkTo
              @route={{@link.route}}
              class="text-oceany-blue-350 hover:text-oceany-blue-325 focus:text-oceany-blue-325 block pb-3 text-base font-semibold print:hidden"
            >
              {{@link.text}}
              <PhosphorIcon @icon="phosphor-arrow-circle-right-fill" />
            </LinkTo>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}

export default SummaryBox
